import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogParams } from '../../../../core/models/dialog-params';
import { ProgressDialogData } from '../../../../core/services/dialog.service';
import {
  BehaviorSubject,
  catchError,
  delay,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { LottieComponent } from 'ngx-lottie';
import { lottieAnimation } from '../../../../core/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const ANIMATION_DURATION = 3000;

@UntilDestroy()
@Component({
  selector: 'ling-progress-dialog',
  standalone: true,
  imports: [CommonModule, LottieComponent],
  templateUrl: './progress-dialog.component.html',
  styleUrls: ['./progress-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef);
  private readonly dialogData =
    inject<DialogParams<ProgressDialogData>>(MAT_DIALOG_DATA).data!;
  protected readonly callState = new BehaviorSubject<
    'loading' | 'success' | 'fail'
  >('loading');
  protected readonly vm$ = this.callState.pipe(
    map((state) => ({
      animation: this.animations[state],
      text: this.text[state](),
    }))
  );
  protected readonly animations = {
    loading: lottieAnimation('loading'),
    success: lottieAnimation('success', { loop: false }),
    fail: lottieAnimation('fail', {
      loop: false,
    }),
  };
  protected readonly text = {
    loading: () =>
      this.dialogData.isMultiple
        ? this.dialogData.text.multiple.loading
        : this.dialogData.text.single.loading,
    success: () =>
      this.dialogData.isMultiple
        ? this.dialogData.text.multiple.success
        : this.dialogData.text.single.success,
    fail: () =>
      this.dialogData.isMultiple
        ? this.dialogData.text.multiple.failure
        : this.dialogData.text.single.failure,
  };

  ngOnInit() {
    of(null)
      .pipe(
        tap(() => this.callState.next('loading')),
        delay(this.dialogData.delay ?? 0),
        switchMap(() => this.dialogData.call),
        untilDestroyed(this),
        take(1),
        tap({
          error: () => this.callState.next('fail'),
          next: () => this.callState.next('success'),
          finalize: () => this.callState.next('success'),
        }),
        catchError((err) => of(err)),
        delay(ANIMATION_DURATION),
        tap({
          error: (res) => this.dialogRef.close(res),
          next: (res) => this.dialogRef.close(res),
          finalize: () => this.dialogRef.close(false),
        })
      )
      .subscribe();
  }
}
